import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../context/auth';
import { PERMISSIONS, Permission } from '../../types';
import { useCan } from '../../queries/permissions';
import MainLoader from '../../components/shared/MainLoader';
import { Card, EmptyState, LinkButton } from '../../components/ui';

interface AdminProtectedRouteProps {
  children?: React.ReactNode;
  /**
   * Guards a single section. `/admin/promo-codes` should be closed to a support
   * agent even though the panel itself is open to them — without this, the only
   * granularity available is "all of /admin or none of it", which is the whole
   * problem this replaces.
   */
  permission?: Permission;
}

/**
 * This component used to destructure `currentUser` and never read it — every
 * route under /admin rendered for anonymous visitors and for ordinary learners.
 * The API calls behind them returned 401/403, so what an unauthorised visitor
 * saw was the whole admin shell in a broken state rather than a closed door.
 *
 * Three outcomes, not one:
 *   - not signed in  → the auth screen, with the attempted path preserved
 *   - signed in, not an admin → 403. Sending them to a login form they are
 *     already past is a dead end, so this is deliberately not a redirect.
 *   - admin → through
 *
 * This is UX. The backend `isAdmin` middleware is the actual boundary.
 */
const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children, permission }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { can, isLoading: permissionsLoading } = useCan();
  const location = useLocation();

  if (authLoading) {
    return <MainLoader />;
  }

  if (!currentUser) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnTo=${returnTo}`} replace />;
  }

  // Signed in, but we do not yet know what they may do. Waiting is the only
  // correct move: guessing "allowed" flashes the panel at a learner, and
  // guessing "denied" shows a 403 to a genuine admin on every cold load.
  if (permissionsLoading) {
    return <MainLoader />;
  }

  const allowed = can(PERMISSIONS.ADMIN_PANEL) && (!permission || can(permission));

  if (!allowed) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-16 font-sans">
        <Card>
          <EmptyState
            icon={<ShieldAlert size={20} aria-hidden="true" />}
            title={
              permission && can(PERMISSIONS.ADMIN_PANEL)
                ? 'You do not have access to this section'
                : 'You do not have access to the admin panel'
            }
            description="If you believe you should have access, ask an administrator to review the roles on your account."
            action={
              <LinkButton to="/" size="sm">
                Back to Mindalitix
              </LinkButton>
            }
          />
        </Card>
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
};

export default AdminProtectedRoute;
